import {
  Box,
  Button,
  Divider,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import FormDialog from "common/Modal/Modal";
import AddIcon from "@mui/icons-material/Add";
import { InventoryItemsTable } from "components/Inventory/InventoryItemsTable";
import { useSnackbar } from "notistack";
import { InventoryServicesTable } from "components/Inventory/InventoryServicesTable";
import { useGetItemsDropdownService } from "subModule/src/services/items/useItems";
import { useGetServicesDropdownService } from "subModule/src/services/service/useService";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  InventoryProfileService,
  TaxProcedureProfileService,
} from "subModule/src/core/services";
import {
  NewPurchaseOrderResponse,
  PurchaseOrderResponse,
  PurchaseOrderStatuses,
} from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { DeleteData } from "subModule/src/type";
import StockReceived from "./StockReceived";
import CreditNote from "./CreditNote";
import Unstock from "./Unstock";
import PurchaseInvoice from "./PurchaseInvoice";
import { GetDefaultTaxProcedureResponse } from "subModule/src/services/taxProcedure/interface";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import {
  useGetPurchaseOrderByIdService,
  useGetPurchaseOrderStatusesService,
} from "subModule/src/services/inventory/useInventory";
// import { ReactComponent as Completed } from "assets/completed.svg";
import PurchasePayment from "./PurchasePayment";
import { GetItemsDropdownResponse } from "subModule/src/services/items/interface";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}

export default function InventorySteps() {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "vendors",
    moduleLabelKey: "Purchase Orders",
    moduleHref: "/inventory/purchase-orders",
    entityFallbackLabelKey: "Purchase Order Number",
    isEdit: !!params?.id,
    action: "details",
  });
  const supplierId = (location.state as { supplier?: number | null })?.supplier;
  const handleBack = () =>
    supplierId ? navigate(-1) : navigate("/inventory/purchase-orders");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const signatureRef = useRef<SignatureCanvas>(null);
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    resetField,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      purchaseOrderItems: [],
      purchaseOrderSvc: [],
      signature: "",
    },
  });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [po, setPO] = useState<PurchaseOrderResponse>();
  const { data: itemsData, refetch: itemsRefetch } = useGetItemsDropdownService(
    {
      pageNumber: dropdownPage,
      size: 20,
      storeId: po?.storeId || 0,
      search: dropdownSearchTerm,
    },
  );
  const { data: servicesData, refetch: servicesRefetch } =
    useGetServicesDropdownService({
      pageNumber: dropdownPage,
      size: 20,
      search: dropdownSearchTerm,
    });
  const { data: poStatusesData, refetch: poStatusesRefetch } =
    useGetPurchaseOrderStatusesService(+(params as any)?.id);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [purchaseOrderItems, setPurchaseOrderItems] = useState<any[]>([]);
  const [purchaseOrderSvc, setPurchaseOrderSvc] = useState<any[]>([]);
  const [entity, setEntity] = useState<FormData>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [checkStatus, setCheckStatus] = useState<number | null>(1);
  const [taxPercentage, setTaxPercentage] = useState<number>(0);
  const [allStatuses, setAllStatuses] = useState<PurchaseOrderStatuses>({});
  const [partsLoader, setPartsLoader] = useState<boolean>(true);
  const { refetch: poByIdFetch } = useGetPurchaseOrderByIdService(params?.id);
  useEffect(() => {
    setLoading(true);
    poStatusesRefetch();
    servicesRefetch();
    if (params?.id) {
      getPOById();
    }
  }, [currentStep]);
  useEffect(() => {
    setAllStatuses(poStatusesData?.data || {});
  }, [poStatusesData]);
  useEffect(() => {
    if (po?.storeId) getParts();
  }, [po?.storeId && currentStep]);
  const getParts = async () => {
    await itemsRefetch()
      .then((res: GetItemsDropdownResponse | unknown | any) => {
        if (res?.data?.pages[0]?.success) {
          ///
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => setPartsLoader(false));
  };
  const getPOById = async () => {
    await poByIdFetch()
      .then((res: NewPurchaseOrderResponse | unknown | any) => {
        if (res?.data?.success) {
          setPO(res.data.data);
          captureLabel(
            res?.data.data?.poNumber != null
              ? String(res.data.data.poNumber)
              : null,
          );
          if (res?.data.data?.status === 1) setPartsLoader(true);
          else setPartsLoader(false);
          setPurchaseOrderItems(res?.data.data?.purchaseOrderItems);
          setPurchaseOrderSvc(res?.data.data?.purchaseOrderSvc);
          setValue("poMemo", res?.data.data?.poMemo);
          setValue("purchaseOrderItems", res?.data.data.purchaseOrderItems);
          setValue("purchaseOrderSvc", res?.data.data.purchaseOrderSvc);
          setCheckStatus(res?.data.data?.status);
          getTaxPercentage(res?.data.data?.taxProcedureId || 0);
          res?.data.data?.stockType === 4
            ? (steps = [
                "Purchase Order",
                "Stock Received",
                "supplierBill",
                "supplierPayment",
              ])
            : (steps = [
                "Purchase Order",
                "supplierBill",
                "supplierPayment",
                "Stock Received",
                // "Debit Note",
                // "Unstock",
              ]);
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        setPartsLoader(false);
      });
    // InventoryProfileService.getPurchaseOrderById(params?.id)
    //   .then((res: NewPurchaseOrderResponse) => {
    //     if (res.success) {
    //       setPO(res.data);
    //       if(res.data?.status === 1) setPartsLoader(true);
    //       else setPartsLoader(false);
    //       setPurchaseOrderItems(res.data?.purchaseOrderItems);
    //       setPurchaseOrderSvc(res.data?.purchaseOrderSvc);
    //       setValue("poMemo", res.data?.poMemo);
    //       setValue("purchaseOrderItems", res.data.purchaseOrderItems);
    //       setValue("purchaseOrderSvc", res.data.purchaseOrderSvc);
    //       setCheckStatus(res.data?.status);
    //       getTaxPercentage(res.data?.taxProcedureId);
    //       res.data?.stockType === 4
    //         ? (steps = ["Purchase Order", "Stock Received", "supplierBill", "supplierPayment"])
    //         : (steps = [
    //             "Purchase Order",
    //             "supplierBill",
    //             "supplierPayment",
    //             "Stock Received",
    //             // "Debit Note",
    //             // "Unstock",
    //           ]);
    //       setLoading(false);
    //     } else {
    //       enqueueSnackbar(<AlertMessages text={res?.message} />, {
    //         variant: "error",
    //       });
    //       setLoading(false);
    //     }
    //   })
    //   .catch((err: any) => {
    //     console.error("===", err);
    //     setLoading(false);
    //   }).finally(() => setPartsLoader(false));
  };
  const getTaxPercentage = (taxId: number | null) => {
    TaxProcedureProfileService.getTaxProcedureById(taxId)
      .then((res: GetDefaultTaxProcedureResponse) => {
        setTaxPercentage((res?.data?.percentageValue || 0) / 100);
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const columns = [
    { id: "itemId" },
    { id: "itemCode" },
    { id: "unitId" },
    {
      id: "itemName",
      label: t("Item") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "supplierSKU",
      label: t("SKU"),
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "unitTitle" },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "total", label: t("Total") },
  ];
  const columnsService = [
    { id: "serviceId" },
    { id: "serviceCode" },
    {
      id: "serviceName",
      label: t("service") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "total", label: t("Total") },
  ];
  const DeleteItem = (row: any, typeName: string) => {
    setLoading(true);
    if (typeName === "purchaseOrderItems") {
      InventoryProfileService.deleteEntityItem(
        (row as any).id,
        "purchaseorder/items",
      )
        .then((response: DeleteData) => {
          if (response.success)
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("deleted") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
          else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
          getPOById();
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
          setLoading(false);
        });
    } else {
      InventoryProfileService.deletePurchaseOrderService((row as any).id)
        .then((response: DeleteData) => {
          if (response.success)
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("deleted") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
          else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
          getPOById();
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
          setLoading(false);
        });
    }
  };
  const addNewItem = (typeName: string) => {
    if (typeName === "purchaseOrderItems") {
      const newItem = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {},
      );
      setPurchaseOrderItems((prevRows) => [...prevRows, newItem]);
    } else {
      const newService = columnsService.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {},
      );
      setPurchaseOrderSvc((prevRows) => [...prevRows, newService]);
    }
  };
  const handleItem = (row: any, typeName: string, action: string) => {
    if (typeName === "purchaseOrderItems") {
      if (action === "create") {
        InventoryProfileService.createPurchaseOrderItem({
          ...row,
          poId: params?.id,
          storeId: po?.storeId,
        })
          .then((response: NewPurchaseOrderResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("Purchase Order Item") +
                    " " +
                    t("created") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getPOById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getPOById();
          });
      } else {
        InventoryProfileService.updatePurchaseOrderItem({
          ...row,
          poId: params?.id,
        })
          .then((response: NewPurchaseOrderResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("Purchase Order Item") +
                    " " +
                    t("updated") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getPOById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getPOById();
          });
      }
    } else {
      if (action === "create") {
        InventoryProfileService.createPurchaseOrderService({
          ...row,
          poId: params?.id,
          storeId: po?.storeId,
        })
          .then((response: NewPurchaseOrderResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("Purchase Order Service") +
                    " " +
                    t("created") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getPOById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getPOById();
          });
      } else {
        InventoryProfileService.updatePurchaseOrderService({
          ...row,
          poId: params?.id,
        })
          .then((response: NewPurchaseOrderResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={
                    t("Purchase Order Service") +
                    " " +
                    t("updated") +
                    " " +
                    t("successfully")
                  }
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getPOById();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getPOById();
          });
      }
    }
  };

  const handleInputChange = (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => {
    if (typeName === "purchaseOrderItems") {
      const updatedItems = [...purchaseOrderItems];
      updatedItems[index][columnId] =
        type === "number" ? parseFloat(value) : value;
      setPurchaseOrderItems(updatedItems);
    } else {
      const updatedServices = [...purchaseOrderSvc];
      updatedServices[index][columnId] = value;
      setPurchaseOrderSvc(updatedServices);
    }
  };
  const onSubmit = (data: FormData) => {
    if (checkStatus === 1) {
      const filteredItems = purchaseOrderItems.filter(
        (item: any) => item?.itemName,
      );
      const filteredServices = purchaseOrderSvc.filter(
        (service: any) => service?.serviceName,
      );
      if (filteredItems.length === 0 && filteredServices.length === 0) {
        enqueueSnackbar(
          <AlertMessages text={t("addAtLeastOneItemService")} />,
          { variant: "error" },
        );
        return;
      }
      setConfirm(true);
      setEntity({
        ...data,
        purchaseOrderItems: filteredItems,
        purchaseOrderSvc: filteredServices,
      });
    } else {
      setCurrentStep(
        (po as any).stockType === 4
          ? steps.indexOf("Stock Received")
          : steps.indexOf("supplierBill"),
      );
    }
  };
  const calculateTotals = () => {
    const itemTotals = purchaseOrderItems.reduce(
      (acc, item) => {
        const unitPrice = parseFloat(item.unitPrice || 0);
        const quantity = parseFloat(item.quantity || 0);
        const discountPercentage = parseFloat(item.discount || 0);
        const discountAmount = (unitPrice * discountPercentage) / 100; // Calculate discount as a percentage of unitPrice
        const totalAfterDiscount = (unitPrice - discountAmount) * quantity; // Calculate total after discount

        return {
          unitPrice: acc.unitPrice + unitPrice,
          subtotal: acc.subtotal + unitPrice * quantity,
          discount: acc.discount + discountAmount * quantity, // Sum of all discounts
          total: acc.total + totalAfterDiscount, // Sum of totals after discount
        };
      },
      { unitPrice: 0, subtotal: 0, discount: 0, total: 0 },
    );

    const serviceTotals = purchaseOrderSvc.reduce(
      (acc, service) => {
        const unitPrice = parseFloat(service.unitPrice || 0);
        const quantity = parseFloat(service.quantity || 0);
        const discountPercentage = parseFloat(service.discount || 0);
        const discountAmount = (unitPrice * discountPercentage) / 100; // Calculate discount as a percentage of unitPrice
        const totalAfterDiscount = (unitPrice - discountAmount) * quantity; // Calculate total after discount

        return {
          unitPrice: acc.unitPrice + unitPrice,
          subtotal: acc.subtotal + unitPrice * quantity,
          discount: acc.discount + discountAmount * quantity, // Sum of all discounts
          total: acc.total + totalAfterDiscount, // Sum of totals after discount
        };
      },
      { unitPrice: 0, subtotal: 0, discount: 0, total: 0 },
    );

    return {
      unitPrice: itemTotals.unitPrice + serviceTotals.unitPrice,
      discount: itemTotals.discount + serviceTotals.discount,
      subTotal: itemTotals.subTotal + serviceTotals.subTotal,
      total:
        itemTotals.total +
        serviceTotals.total +
        (po as PurchaseOrderResponse)?.taxAmount,
    };
  };
  const totals = calculateTotals();
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
    table?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (table === "inventoryServices") {
        servicesRefetch().finally(() => setSearchLoading(null));
        return;
      }
      itemsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={handleBack}
        onSubmit={() => {}}
        heading={t("Purchase Orders")}
        icon={
          <ShoppingCartOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
      />
      {loading ? (
        <Loader />
      ) : currentStep === steps.indexOf("Purchase Order") ? (
        <Grid
          container
          component="form"
          id="form"
          className="px-[8px] items-baseline"
          rowGap={2.5}
          columnGap={4}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container className="items-center ">
            <Grid item xs={12} sm={6} className="text-start">
              <Typography
                className="font-Saira text-[15px] font-semibold"
                style={{ color: themeColors?.primary }}
              >
                {t(steps[currentStep])}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} className="hide-on-print">
              <div className="flex gap-2 justify-end">
                {checkPrivileges("ROLE_PURCHASEORDER_AUTHORIZE") && (
                  <Button
                    type="submit"
                    className="primary"
                    style={{ background: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() => onSubmit}
                  >
                    {checkStatus === 1 ? t("Authorize") : t("Next")}
                  </Button>
                )}
                {/*  <Button className="secondary" variant="contained" disableElevation>{t('email')}</Button> */}
                {checkStatus === 2 ? (
                  <Button
                    className="secondary"
                    style={{ color: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() =>
                      navigate(`${window.location.pathname}/print`)
                    }
                  >
                    {t("Print")}
                  </Button>
                ) : null}
                <Button
                  className="secondary"
                  style={{ color: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={handleBack}
                >
                  {t("Back to list")}
                </Button>
              </div>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider className="bg-[#AEB3C4] hide-on-print" />
          </Grid>
          <Grid
            container
            className="py-[12px] px-[18px] rounded-[8px] items-center"
            style={{ background: themeColors?.primary }}
          >
            <Grid item xs={4}>
              <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
                {t(steps[currentStep])}
              </Typography>
              <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                <span className="font-bold">{t(steps[currentStep])} # </span>{" "}
                <span>{po?.poNumber || ""}</span>
              </Typography>
              <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                <span className="font-bold">{t("Date")}</span>{" "}
                <span>{po?.poDate?.split("T")[0] || ""}</span>
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <div className="flex justify-end gap-10">
                {po?.urgencyStatus ? (
                  <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                    <span className="font-bold">{t("Priority")}</span> <br />
                    <span
                      className={`font-medium rounded-[6px] ${
                        po?.urgencyStatus === 10
                          ? "text-green-500 bg-[#E6F6E9] px-[10px]"
                          : po?.urgencyStatus === 20
                            ? "text-orange-500 bg-[#F0F0F0] px-[5px]"
                            : po?.urgencyStatus === 30
                              ? "text-red-500 bg-[#FFE5E5] px-[10px]"
                              : ""
                      }`}
                    >
                      {po?.urgencyStatus === 10
                        ? t("Low")
                        : po?.urgencyStatus === 20
                          ? t("Medium")
                          : po?.urgencyStatus === 30
                            ? t("High")
                            : ""}
                    </span>
                  </Typography>
                ) : null}
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">
                    {t("Supplier")} {t("name")}
                  </span>{" "}
                  <br />
                  <span className="font-medium">{po?.vendorTitle || ""}</span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Requested")}</span> <br />
                  <span className="font-medium">{po?.totalQuantity || 0}</span>
                </Typography>
                {/* <Typography className="font-Saira text-[11px] text-[#fff] text-center"><span className="font-bold">{t('Received')}</span> <br /><span className="font-medium">{po?.totalQuantity || 0}</span></Typography> */}
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Subtotal")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.subtotalAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Discount")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.discount || 0,
                    ) || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("tax")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.taxAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Total")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.totalAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
              </div>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              {partsLoader ? (
                <Box sx={{ "& .MuiBackdrop-root": { zIndex: 999 } }}>
                  <Loader />
                </Box>
              ) : null}
              <NewPurchaseSteps
                form={{
                  control,
                  register,
                  handleSubmit,
                  setValue,
                  errors,
                  watch,
                  resetField,
                }}
                addNewItem={addNewItem}
                handleInputChange={handleInputChange}
                items={itemsData?.pages[0]?.data?.content || []}
                services={servicesData?.pages[0]?.data?.content || []}
                DeleteItem={DeleteItem}
                handleItem={handleItem}
                {...{
                  currentStep,
                  setCurrentStep,
                  columns,
                  columnsService,
                  purchaseOrderItems,
                  purchaseOrderSvc,
                  loggedInUserCurrency,
                  params,
                  checkStatus,
                  t,
                  themeColors,
                  allStatuses,
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
                itemsData={itemsData || {}}
                handleDropdownActions={handleDropdownActions}
                servicesData={servicesData || {}}
              />
            </Grid>
          </Grid>
          <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left mb-[-5px]">
            {t("Purchase Order Memo")}
          </Typography>
          <Grid container xs={12} spacing={1}>
            <Grid item xs={12} md={6}>
              <TextField
                {...register("poMemo")}
                placeholder={t("Purchase Order Memo")}
                title={t("Purchase Order Memo")}
                fullWidth
                error={!!errors.poMemo}
                helperText={errors.poMemo?.message as string}
                InputLabelProps={{ shrink: true }}
                multiline
                rows={5}
                sx={{
                  "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                    width: "100%",
                  },
                }}
                disabled={checkStatus === 2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                className="bg-[#D8D1E0] rounded-[8px]"
                sx={{
                  border: "1px solid #5f6770",
                  width: "calc(100% + 8px)",
                  padding: "8px 14px",
                }}
              >
                <Box className="flex justify-end gap-12 mt-[9px]">
                  <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                    {t("Subtotal")}
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                    <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                    {new Intl.NumberFormat("en-US", {}).format(
                      totals.subTotal ||
                        (po as PurchaseOrderResponse)?.subtotalAmount ||
                        0,
                    )}
                  </Typography>
                </Box>
                <Box className="flex justify-end gap-12 mt-[9px]">
                  <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                    {t("Discount")}
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                    <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                    {new Intl.NumberFormat("en-US", {}).format(
                      totals.discount ||
                        (po as PurchaseOrderResponse)?.discount ||
                        0,
                    )}
                  </Typography>
                </Box>
                <Box className="flex justify-end gap-12 mt-[9px]">
                  <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                    {t("tax") + ` (${taxPercentage * 100}%)`}
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                    <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.taxAmount || 0,
                    )}
                  </Typography>
                </Box>
                <Box className="flex justify-end gap-12 mt-[9px]">
                  <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[auto]">
                    {t("totalWithTax")}
                  </Typography>
                  <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[20%] text-end">
                    <span className="uppercase">{loggedInUserCurrency}</span>{" "}
                    {new Intl.NumberFormat("en-US", {}).format(
                      totals.total ||
                        (po as PurchaseOrderResponse)?.totalAmount ||
                        0,
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={12} className="hide-on-print">
            <Divider className="bg-[#AEB3C4] mt-[8px]" />
          </Grid>
          <Grid container className="items-center hide-on-print">
            <Grid item xs={12} sm={6} className="text-start"></Grid>
            <Grid item xs={12} sm={6}>
              <div className="flex gap-2 justify-end">
                {checkPrivileges("ROLE_PURCHASEORDER_AUTHORIZE") && (
                  <Button
                    type="submit"
                    className="primary"
                    style={{ background: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() => onSubmit}
                  >
                    {checkStatus === 1 ? t("Authorize") : t("Next")}
                  </Button>
                )}
                {/*  <Button className="secondary" variant="contained" disableElevation>{t('email')}</Button> */}
                {checkStatus === 2 ? (
                  <Button
                    className="secondary"
                    style={{ color: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() =>
                      navigate(`${window.location.pathname}/print`)
                    }
                  >
                    {t("Print")}
                  </Button>
                ) : null}
                <Button
                  className="secondary"
                  style={{ color: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={handleBack}
                >
                  {t("Back to list")}
                </Button>
              </div>
            </Grid>
          </Grid>
          {confirm && (
            <FormDialog
              open={confirm}
              onClose={(event, reason) => {
                if (event && reason !== "backdropClick") {
                  setConfirm(false);
                  resetField("signature");
                  signatureRef.current?.clear();
                }
              }}
              title={t("Authorize") + " " + t(steps[currentStep])}
              saveFunction={async () => {
                const isValid = await trigger("signature");
                if (!isValid) return;
                const signature = watch("signature");
                delete entity.typeName;
                entity.purchaseOrderItems.every((row: any) => delete row.total);
                entity.purchaseOrderSvc.every((row: any) => delete row.total);
                setConfirm(false);
                InventoryProfileService.changeStatusPurchaseOrder({
                  entityId: params.id,
                  status: 2,
                  signature,
                })
                  .then((response: NewPurchaseOrderResponse) => {
                    if (response.success) {
                      setConfirm(false);
                      currentStep + 1 === steps.length
                        ? setCurrentStep(currentStep)
                        : setCurrentStep(
                            (po as any).stockType === 4
                              ? steps.indexOf("Stock Received")
                              : steps.indexOf("supplierBill"),
                          );
                      getPOById();
                      enqueueSnackbar(
                        <AlertMessages
                          statusCode={response.status}
                          text={
                            t("Status") +
                            " " +
                            t("changed") +
                            " " +
                            t("successfully")
                          }
                        />,
                        { variant: "success" },
                      );
                    } else {
                      setConfirm(false);
                      enqueueSnackbar(
                        <AlertMessages text={response?.message} />,
                        {
                          variant: "error",
                        },
                      );
                    }
                  })
                  .catch((err: any) => {
                    console.error("===", err);
                    setConfirm(false);
                    enqueueSnackbar(
                      <AlertMessages
                        text={
                          err?.message ||
                          err?.data?.message ||
                          "Something went wrong"
                        }
                      />,
                      { variant: "error" },
                    );
                  });
              }}
              content={
                <>
                  <Typography>{t("areYouSureUneditable")}</Typography>
                  <Controller
                    name="signature"
                    control={control}
                    rules={{ required: t("This field is required.") }}
                    render={({
                      field: { onChange },
                      fieldState: { error },
                    }) => (
                      <Box className="mt-2">
                        <Typography className="font-Saira text-[13px] font-semibold">
                          {t("signaturePlaceholder")} *
                        </Typography>
                        <Box>
                          <Box>
                            <Box
                              className="rounded-[4px]"
                              sx={{
                                border: `2px solid ${error ? "#d32f2f" : "#AEB3C4"}`,
                                width: "270px",
                                height: "120px",
                                overflow: "hidden",
                              }}
                            >
                              <SignatureCanvas
                                ref={signatureRef}
                                clearOnResize={false}
                                canvasProps={{
                                  width: 270,
                                  height: 120,
                                }}
                                onEnd={() => {
                                  if (
                                    signatureRef.current &&
                                    !signatureRef.current.isEmpty()
                                  ) {
                                    onChange(signatureRef.current.toDataURL());
                                  }
                                }}
                                penColor="red"
                              />
                            </Box>
                            {error && (
                              <Typography
                                className="text-[11px] mt-1 absolute"
                                color="error"
                              >
                                {error.message}
                              </Typography>
                            )}
                          </Box>
                          <Button
                            sx={{
                              boxShadow: "none",
                              color: themeColors?.primary,
                            }}
                            className={`font-Saira font-[600] text-[13px] bg-[#d8d1e0] h-[38px] px-8 py-1 rounded-[8px] absolute bottom-[10px] left-[25px]`}
                            variant="contained"
                            disableElevation
                            type="button"
                            onClick={() => {
                              signatureRef.current?.clear();
                              onChange("");
                            }}
                          >
                            {t("Clear")}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  />
                </>
              }
              submitButtonText="OK"
            />
          )}
        </Grid>
      ) : currentStep === steps.indexOf("supplierBill") ? (
        <PurchaseInvoice steps={steps} setCurrentSteps={setCurrentStep} />
      ) : currentStep === steps.indexOf("supplierPayment") ? (
        <PurchasePayment steps={steps} setCurrentSteps={setCurrentStep} />
      ) : currentStep === steps.indexOf("Stock Received") ? (
        <StockReceived steps={steps} setCurrentSteps={setCurrentStep} />
      ) : currentStep === steps.indexOf("Debit Note") ? (
        <CreditNote steps={steps} setCurrentSteps={setCurrentStep} />
      ) : (
        <Unstock steps={steps} setCurrentSteps={setCurrentStep} />
      )}
    </>
  );
}

let steps = [
  "Purchase Order",
  "supplierBill",
  "supplierPayment",
  "Stock Received",
  // "Debit Note",
  // "Unstock",
];

interface NewPurchaseStepsProps {
  form: FormData;
  addNewItem: (typeName: string) => void;
  handleInputChange: (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => void;
  currentStep: number;
  setCurrentStep: any;
  columns: any[];
  columnsService: any[];
  purchaseOrderItems: any[];
  purchaseOrderSvc: any[];
  items: any[];
  services: any[];
  loggedInUserCurrency?: string;
  DeleteItem: (row: any, typeName: string) => void;
  params?: any;
  handleItem?: (row: any, typeName: string, action: string) => void;
  checkStatus?: number | null;
  t?: any;
  themeColors?: any;
  allStatuses?: PurchaseOrderStatuses;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  dropdownPage: number;
  searchLoading: string | null;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  setDropdownPage: any;
  itemsData: any;
  servicesData: any;
}

export const NewPurchaseSteps: React.FC<NewPurchaseStepsProps> = ({
  form,
  addNewItem,
  handleInputChange,
  currentStep,
  setCurrentStep,
  columns,
  columnsService,
  purchaseOrderItems,
  purchaseOrderSvc,
  items,
  services,
  loggedInUserCurrency,
  DeleteItem,
  params,
  handleItem,
  checkStatus,
  t,
  themeColors,
  allStatuses,
  servicesData,
  handleDropdownActions,
  itemsData,
  dropdownPage,
  searchLoading,
  dropdownSearchTerm,
  setDropdownSearchTerm,
  setDropdownPage,
}) => {
  return (
    <>
      <Stepper
        className="hide-on-print"
        activeStep={currentStep}
        connector={<></>}
        sx={{
          "& .MuiStep-horizontal .Mui-active + .MuiStepLabel-labelContainer": {
            background: `linear-gradient(20.78deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
          },
        }}
      >
        {steps.map((label, index) => {
          const statusKeyMap: Record<string, keyof PurchaseOrderStatuses> = {
            "Purchase Order": "purchaseOrder",
            supplierBill: "purchaseInvoice",
            "Stock Received": "stockReceive",
            "Debit Note": "purchaseCreditDebitNote",
            Unstock: "unStock",
          };
          const stepProps: { completed?: boolean } = {};
          const labelProps: { optional?: React.ReactNode } = {};
          const key = statusKeyMap[label];
          const isCompleted =
            allStatuses?.[key] === 2 ||
            allStatuses?.[key] === 3 ||
            allStatuses?.[key] === 4;
          const isSupplierBillCompleted =
            allStatuses?.purchaseInvoice === 2 ||
            allStatuses?.purchaseInvoice === 3 ||
            allStatuses?.purchaseInvoice === 4;
          return (
            <Step
              key={index}
              completed={isCompleted}
              {...stepProps}
              onClick={() => {
                if (
                  document
                    .querySelector(`.MuiStep-root:nth-child(${index + 1})`)
                    ?.classList.contains("Mui-completed") ||
                  (isSupplierBillCompleted &&
                    (label === "supplierPayment" || label === "Stock Received"))
                ) {
                  setCurrentStep(steps.indexOf(label));
                }
              }}
              className={`flex items-center ${document.querySelector(`.MuiStep-root:nth-child(${index + 1})`)?.classList.contains("Mui-completed") ? "cursor-pointer" : ""}`}
            >
              {/* {document.querySelector(`.MuiStep-root:nth-child(${index + 1})`)?.classList.contains('Mui-completed') ? <Completed style={{ fill: themeColors?.primary, width: '15px', height: '15px' }} /> : null} */}
              <StepLabel {...labelProps}>{t(label)}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box className="bg-white py-[12px] px-[18px] rounded-[8px] mt-[16px]">
        <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left">
          {t(steps[currentStep])}
        </Typography>
        <InventoryItemsTable
          form={form}
          columns={columns}
          handleInputChange={handleInputChange}
          rows={purchaseOrderItems}
          typeName="purchaseOrderItems"
          items={items}
          currency={loggedInUserCurrency}
          DeleteItem={DeleteItem}
          handleItem={handleItem}
          itemsResponse={itemsData || {}}
          handleDropdownActions={handleDropdownActions}
          {...{
            params,
            checkStatus,
            dropdownPage,
            searchLoading,
            dropdownSearchTerm,
            setDropdownSearchTerm,
            setDropdownPage,
          }}
        />
        <Grid item xs={12} sm={6} className="hide-on-print">
          <div className="flex gap-2 justify-start">
            <Button
              className="secondary flex gap-1"
              style={{ color: themeColors?.primary }}
              variant="contained"
              disableElevation
              disabled={checkStatus === 2}
              onClick={() => addNewItem("purchaseOrderItems")}
            >
              <AddIcon className="w-[20px] h-[20px]" /> {t("Add More Items")}
            </Button>
            {/* <Button className="secondary" variant="contained" disableElevation disabled={checkStatus === 2}>{t('Import')}</Button>
                    <Button className="secondary" variant="contained" disableElevation disabled={checkStatus === 2}>{t("Export")}</Button> */}
          </div>
        </Grid>
      </Box>
      <Box className="flex gap-2 justify-start mt-[16px]">
        <Button
          className="secondary cursor-text"
          style={{ color: themeColors?.primary }}
          variant="contained"
          disableElevation
        >
          {t("Additional Costs")}
        </Button>
      </Box>
      <Box className="bg-white py-[12px] px-[18px] rounded-[8px] mt-[16px]">
        <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left">
          {t("Additional Cost")}
        </Typography>
        <InventoryServicesTable
          form={form}
          columns={columnsService}
          handleInputChange={handleInputChange}
          rows={purchaseOrderSvc}
          typeName="purchaseOrderSvc"
          services={services}
          currency={loggedInUserCurrency}
          DeleteItem={DeleteItem}
          handleItem={handleItem}
          {...{ params, checkStatus }}
          servicesResponse={servicesData || {}}
          handleDropdownActions={handleDropdownActions}
          {...{
            dropdownPage,
            searchLoading,
            dropdownSearchTerm,
            setDropdownSearchTerm,
            setDropdownPage,
          }}
        />
        <Grid item xs={12} sm={6} className="hide-on-print">
          <div className="flex gap-2 justify-start">
            <Button
              className="secondary flex gap-1"
              style={{ color: themeColors?.primary }}
              variant="contained"
              disableElevation
              disabled={checkStatus === 2}
              onClick={() => addNewItem("purchaseOrderSvc")}
            >
              <AddIcon className="w-[20px] h-[20px]" /> {t("Add More Items")}
            </Button>
          </div>
        </Grid>
        <Grid item xs={12}>
          <Divider className="bg-[#AEB3C4] mt-[15px] mb-[8px]" />
        </Grid>
      </Box>
    </>
  );
};
